/** Monta board / gantt / chips do mock a partir do snapshot SQL do dia. */

import { bindTechnicianPanelDomTips } from './technicianPanelDomTip';

const HOUR_START = 7;
const HOUR_END = 19;
const DAY_SPAN = (HOUR_END - HOUR_START) * 60;

export const FILTER_TRAVEL = '__travel__';
export const FILTER_LUNCH = '__lunch__';

const TRAVEL_META = {
  label: 'Deslocamento',
  color: '#a8a8b4',
  seg: '#c9cad4',
  bg: '#f7f7f9',
  border: '#e4e4ea',
};

/** Alinhado ao rastreio / LCMY_GO_TRAVEL outcome=redirected. */
const REDIRECTED_TRAVEL_META = {
  label: 'Deslocamento redirecionado',
  color: '#ea580c',
  seg: '#fb923c',
  bg: '#fff7ed',
  border: '#fdba74',
};

/** Alinhado a LiveMap / LUNCH_COLOR backend (#f59e0b). */
const LUNCH_META = {
  label: 'Almoço',
  color: '#d97706',
  seg: '#fbbf24',
  bg: '#fffbeb',
  border: '#fde68a',
};

/**
 * Paleta de condições (ativo + fundo pastel + borda), na ordem:
 * 1 Roxo Ametista & Lavanda
 * 2 Turquesa & Menta
 * 3 Coral Energético & Pêssego Pálido
 * 4 Magenta & Quartzo Suave
 * 5 Verde Floresta & Espuma do Mar
 * 6 Carvão & Gelo
 */
const COND_PALETTE = [
  { color: '#6d54e8', bg: '#faf9ff', border: '#e1ddfb' },
  { color: '#0d8f9e', bg: '#f4fbfc', border: '#cfe9ed' },
  { color: '#e85d3b', bg: '#fff5f0', border: '#f5d4c8' },
  { color: '#c2185b', bg: '#fdf2f7', border: '#f0c8d8' },
  { color: '#1f6b45', bg: '#f0faf4', border: '#c5e6d4' },
  { color: '#3d3d49', bg: '#f5f6f8', border: '#e0e2e8' },
];

/** Atribui cores na ordem da paleta (1ª condição → roxo, 2ª → turquesa, …). */
function buildCondColorMap(entries) {
  const counts = new Map();
  entries.forEach((e) => {
    if (e.kind !== 'os') return;
    const key = e.condNome || 'Sem condição';
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const ordered = [...counts.keys()].sort(
    (a, b) => counts.get(b) - counts.get(a) || a.localeCompare(b, 'pt-BR')
  );
  const map = new Map();
  ordered.forEach((name, i) => {
    map.set(name, COND_PALETTE[i % COND_PALETTE.length]);
  });
  return map;
}

export function condMetaFromNome(condNome, colorMap) {
  const key = (condNome && String(condNome).trim()) || 'Sem condição';
  if (colorMap && colorMap.has(key)) return colorMap.get(key);
  return COND_PALETTE[0];
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDurMins(mins) {
  if (mins == null || !Number.isFinite(mins) || mins < 0) return '—';
  return `${Math.round(mins)}min`;
}

function parseHhMmToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function leftPct(minsFromMidnight) {
  if (minsFromMidnight == null || !Number.isFinite(minsFromMidnight)) return null;
  const from7 = minsFromMidnight - HOUR_START * 60;
  return Math.max(0, Math.min(100, (from7 / DAY_SPAN) * 100));
}

function widthPct(durMin) {
  if (durMin == null || !Number.isFinite(durMin) || durMin <= 0) return 1.5;
  return Math.max(1.2, Math.min(100, (durMin / DAY_SPAN) * 100));
}

function hourBucket(minsFromMidnight) {
  if (minsFromMidnight == null) return null;
  const h = Math.floor(minsFromMidnight / 60);
  if (h < HOUR_START || h > HOUR_END) return null;
  return h;
}

export function buildEntries(dayData) {
  const osRows = dayData?.os || [];
  const travels = dayData?.deslocamentos || [];
  const entries = [];

  osRows.forEach((row) => {
    const emServico = Boolean(row.emServico) || String(row.statusOs || '').toUpperCase() === 'EM SERVICO';
    if (!row.dataFim && !emServico) return;

    const startMin = parseHhMmToMinutes(row.horaIni);
    const fora =
      !emServico &&
      (String(row.statusRoteiro || '').toUpperCase().includes('NÃO') ||
        String(row.statusRoteiro || '').toUpperCase().includes('NAO'));

    if (emServico) {
      let durMin = row.duracaoMin;
      if (durMin == null && row.horaIni) {
        const now = new Date();
        const nowHhMm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        const [ih, im] = row.horaIni.split(':').map(Number);
        const [fh, fm] = nowHhMm.split(':').map(Number);
        durMin = fh * 60 + fm - (ih * 60 + im);
        if (durMin < 0) durMin += 24 * 60;
      }
      entries.push({
        kind: 'os',
        emServico: true,
        sortMin: startMin ?? 24 * 60,
        hour: hourBucket(startMin),
        os: row.os,
        equip: row.equipamento || '—',
        client: row.cliente || '—',
        horaIni: row.horaIni,
        horaFim: null,
        dur: row.tempoAndamento || formatDurMins(durMin),
        durMin: durMin ?? 20,
        fora: false,
        meta: { color: TRAVEL_META.color, bg: TRAVEL_META.bg, border: TRAVEL_META.border },
        condNome: null,
        hasPendencia: false,
      });
      return;
    }

    const condNome = (row.condNome && String(row.condNome).trim()) || 'Sem condição';
    const hasPendencia = Boolean(
      (row.condOperacao && String(row.condOperacao).trim()) ||
        (row.condRetorno && String(row.condRetorno).trim())
    );
    entries.push({
      kind: 'os',
      emServico: false,
      sortMin: startMin ?? 24 * 60,
      hour: hourBucket(startMin),
      os: row.os,
      equip: row.equipamento || '—',
      client: row.cliente || '—',
      horaIni: row.horaIni,
      horaFim: row.horaFim,
      dur: formatDurMins(row.duracaoMin),
      durMin: row.duracaoMin,
      fora,
      meta: null,
      condNome,
      hasPendencia,
    });
  });

  travels.forEach((row, i) => {
    const startMin = parseHhMmToMinutes(row.horaIni);
    if (startMin == null) return;
    const emAndamento = Boolean(row.emAndamento) || (row.horaIni && !row.horaFim);
    let durMin = row.duracaoMin;
    if (emAndamento && durMin == null && row.horaIni) {
      const now = new Date();
      const nowHhMm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const [ih, im] = row.horaIni.split(':').map(Number);
      const [fh, fm] = nowHhMm.split(':').map(Number);
      durMin = fh * 60 + fm - (ih * 60 + im);
      if (durMin < 0) durMin += 24 * 60;
    }
    const codes = Array.isArray(row.operationCodes)
      ? row.operationCodes.map((c) => String(c).trim()).filter(Boolean)
      : row.os
        ? [String(row.os).trim()]
        : [];
    const redirected =
      !!row.redirected || String(row.outcome || '').toLowerCase() === 'redirected';
    entries.push({
      kind: 'travel',
      sortMin: startMin,
      hour: hourBucket(startMin),
      id: `travel-${i}-${row.tripId || row.os || ''}`,
      client: row.cliente || '—',
      horaIni: row.horaIni,
      horaFim: row.horaFim,
      dur: emAndamento
        ? row.tempoAndamento || formatDurMins(durMin)
        : formatDurMins(durMin),
      durMin: durMin ?? 20,
      os: codes[0] || row.os || null,
      operationCodes: codes,
      redirected,
      tripId: row.tripId || null,
      emAndamento,
    });
  });

  const lunches = dayData?.almoco || [];
  lunches.forEach((row, i) => {
    const startMin = parseHhMmToMinutes(row.horaIni);
    if (startMin == null) return;
    const emAndamento = Boolean(row.emAndamento) || (row.horaIni && !row.horaFim);
    let durMin = row.duracaoMin;
    if (emAndamento && (durMin == null || !Number.isFinite(durMin)) && row.horaIni) {
      const now = new Date();
      const nowHhMm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const [ih, im] = row.horaIni.split(':').map(Number);
      const [fh, fm] = nowHhMm.split(':').map(Number);
      durMin = fh * 60 + fm - (ih * 60 + im);
      if (durMin < 0) durMin += 24 * 60;
    }
    entries.push({
      kind: 'lunch',
      sortMin: startMin,
      hour: hourBucket(startMin),
      id: `lunch-${i}`,
      horaIni: row.horaIni,
      horaFim: row.horaFim,
      dur: emAndamento
        ? row.tempoAndamento || formatDurMins(durMin)
        : formatDurMins(durMin),
      durMin: durMin ?? 20,
      emAndamento,
    });
  });

  const colorMap = buildCondColorMap(entries.filter((e) => e.kind === 'os' && !e.emServico));
  entries.forEach((e) => {
    if (e.kind === 'os' && !e.emServico) e.meta = condMetaFromNome(e.condNome, colorMap);
  });

  entries.sort((a, b) => a.sortMin - b.sortMin);
  return entries;
}

function filterEntries(entries, filterKey) {
  if (!filterKey) return entries;
  if (filterKey === FILTER_TRAVEL) return entries.filter((e) => e.kind === 'travel');
  if (filterKey === FILTER_LUNCH) return entries.filter((e) => e.kind === 'lunch');
  return entries.filter((e) => e.kind === 'os' && !e.emServico && e.condNome === filterKey);
}

const WRENCH_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>';

function renderOsCard(e) {
  if (e.emServico) {
    const clock = e.horaIni ? `${esc(e.horaIni)} → …` : '—';
    return `<div class="tpm-card tpm-card-travel is-live tpm-card-inservice" data-tpm="os" data-os="${esc(e.os || '')}" data-live="1" data-status="em-servico">
      <div class="tpm-card-top"><span class="tpm-os">${esc(e.os || '—')}</span><span class="tpm-spacer"></span><span class="tpm-dur">${esc(e.dur)}</span></div>
      <span class="tpm-equip">${esc(e.equip)}</span>
      <span class="tpm-client">${esc(e.client)}</span>
      <span class="tpm-clock">${clock}</span>
    </div>`;
  }

  const foraHtml = e.fora ? '<span class="tpm-fora">fora</span>' : '';
  const clock =
    e.horaIni && e.horaFim
      ? `${esc(e.horaIni)} → ${esc(e.horaFim)}`
      : e.horaIni
        ? esc(e.horaIni)
        : '—';
  return `<div class="tpm-card tpm-card-os is-clickable" data-tpm="os" data-os="${esc(e.os || '')}" data-cond="${esc(e.condNome)}" role="button" tabindex="0" style="border-color:${e.meta.border};background:${e.meta.bg};--tpm-cond:${e.meta.color}">
      <div class="tpm-card-top"><span class="tpm-os">${esc(e.os || '—')}</span>${foraHtml}<span class="tpm-spacer"></span><span class="tpm-dur" style="color:${e.meta.color}">${esc(e.dur)}</span></div>
      <span class="tpm-equip">${esc(e.equip)}</span>
      <span class="tpm-client">${esc(e.client)}</span>
      <span class="tpm-clock">${clock}</span>
    </div>`;
}

function travelOsLabel(e) {
  const codes = Array.isArray(e.operationCodes) && e.operationCodes.length
    ? e.operationCodes
    : e.os
      ? [e.os]
      : [];
  return codes.map((c) => `#${c}`).join(', ');
}

function travelCardLabel(e) {
  const osLabel = travelOsLabel(e);
  if (e.redirected) {
    return osLabel ? `Deslocamento redirecionado ${osLabel}` : 'Deslocamento redirecionado';
  }
  return osLabel ? `Deslocamento ${osLabel}` : 'Deslocamento';
}

function renderTravelCard(e) {
  const pulse = e.emAndamento ? ' is-live' : '';
  const redirectedCls = e.redirected ? ' is-redirected' : '';
  const clock = e.emAndamento
    ? `${esc(e.horaIni || '—')} → …`
    : e.horaIni && e.horaFim
      ? `${esc(e.horaIni)} → ${esc(e.horaFim)}`
      : e.horaIni
        ? esc(e.horaIni)
        : '—';
  const durLabel = e.emAndamento ? esc(e.dur || '…') : esc(e.dur);
  const label = travelCardLabel(e);
  const title = e.emAndamento
    ? `${esc(label)} · desde ${esc(e.horaIni || '—')}`
    : `${esc(label)} · ${esc(e.dur)}`;
  const durStyle = e.redirected ? ` style="color:${REDIRECTED_TRAVEL_META.color}"` : '';
  return `<div class="tpm-card tpm-card-travel${pulse}${redirectedCls}" data-tpm="travel" data-kind="travel" data-live="${e.emAndamento ? '1' : '0'}" data-redirected="${e.redirected ? '1' : '0'}" data-tpm-tip="${title}">
        <div class="tpm-card-top"><span class="tpm-travel-label">${esc(label)}</span><span class="tpm-dur"${durStyle}>${durLabel}</span></div>
        <span class="tpm-sub">${esc(e.client)}</span>
        <span class="tpm-clock">${clock}</span>
      </div>`;
}

function renderLunchCard(e) {
  const pulse = e.emAndamento ? ' is-live' : '';
  const clock = e.emAndamento
    ? `${esc(e.horaIni || '—')} → …`
    : e.horaIni && e.horaFim
      ? `${esc(e.horaIni)} → ${esc(e.horaFim)}`
      : e.horaIni
        ? esc(e.horaIni)
        : '—';
  const durLabel = e.emAndamento ? esc(e.dur || '…') : esc(e.dur);
  const title = e.emAndamento
    ? `Almoço em andamento · desde ${esc(e.horaIni || '—')}`
    : `Almoço · ${esc(e.dur)}`;
  return `<div class="tpm-card tpm-card-lunch${pulse}" data-tpm="lunch" data-kind="lunch" data-live="${e.emAndamento ? '1' : '0'}" data-tpm-tip="${title}">
        <div class="tpm-card-top"><span class="tpm-lunch-label">Almoço</span><span class="tpm-dur" style="color:${LUNCH_META.color}">${durLabel}</span></div>
        <span class="tpm-clock">${clock}</span>
      </div>`;
}

function renderEntryCard(e) {
  if (e.kind === 'os') return renderOsCard(e);
  if (e.kind === 'lunch') return renderLunchCard(e);
  return renderTravelCard(e);
}

function renderBoard(entries) {
  const byHour = new Map();
  for (let h = HOUR_START; h <= HOUR_END; h += 1) byHour.set(h, []);

  entries.forEach((e) => {
    let h = e.hour;
    if (h == null) {
      const startMin = parseHhMmToMinutes(e.horaIni);
      if (startMin != null) h = Math.floor(startMin / 60);
    }
    if (h == null) return;
    if (h < HOUR_START) h = HOUR_START;
    if (h > HOUR_END) h = HOUR_END;
    byHour.get(h)?.push(e);
  });

  const filledHours = [];
  for (let h = HOUR_START; h <= HOUR_END; h += 1) {
    if ((byHour.get(h) || []).length) filledHours.push(h);
  }

  if (!filledHours.length) {
    // Sem cards: ainda exibe 07h→19h vazias
    const partsEmpty = [];
    for (let h = HOUR_START; h <= HOUR_END; h += 1) {
      const label = `${String(h).padStart(2, '0')}h`;
      partsEmpty.push(`<div class="tpm-hour-col" data-tpm="hour-col" data-hour="${label}">
    <div class="tpm-hour-head"><span>${label}</span><span class="tpm-hour-count">0</span></div>
    <div class="tpm-hour-body"></div>
  </div>`);
    }
    return partsEmpty.join('');
  }

  const firstFilled = filledHours[0];
  const lastFilled = filledHours[filledHours.length - 1];
  const parts = [];

  // Gap à esquerda: se o 1º atendimento for 8h ou depois (desde 7h)
  if (firstFilled > HOUR_START) {
    const skipped = firstFilled - HOUR_START;
    const gapLabel = `${skipped}h sem atendimento`;
    const gapTitle = `${gapLabel} antes das ${String(firstFilled).padStart(2, '0')}h`;
    parts.push(
      `<div class="tpm-gap" data-tpm-tip="${esc(gapTitle)}" data-tpm="gap"><span class="tpm-gap-label">${esc(gapLabel)}</span><span class="tpm-gap-line"></span></div>`
    );
  }

  // Colunas com cards + gaps internos entre saltos
  let prevHour = null;
  for (let i = 0; i < filledHours.length; i += 1) {
    const h = filledHours[i];
    const list = byHour.get(h) || [];

    if (prevHour != null) {
      const skipped = h - prevHour - 1;
      if (skipped > 0) {
        const gapLabel = `${skipped}h sem atendimento`;
        const gapTitle = `${gapLabel} antes das ${String(h).padStart(2, '0')}h`;
        parts.push(
          `<div class="tpm-gap" data-tpm-tip="${esc(gapTitle)}" data-tpm="gap"><span class="tpm-gap-label">${esc(gapLabel)}</span><span class="tpm-gap-line"></span></div>`
        );
      }
    }

    const label = `${String(h).padStart(2, '0')}h`;
    const nOs = list.filter((x) => x.kind === 'os').length;
    const countCls = nOs ? 'tpm-hour-count is-os' : 'tpm-hour-count';
    const cards = list.map((e) => renderEntryCard(e)).join('');
    parts.push(`<div class="tpm-hour-col" data-tpm="hour-col" data-hour="${label}">
    <div class="tpm-hour-head"><span>${label}</span><span class="${countCls}">${list.length}</span></div>
    <div class="tpm-hour-body">${cards}</div>
  </div>`);
    prevHour = h;
  }

  // À direita: sempre as colunas restantes até 19h (mesmo vazias)
  for (let h = lastFilled + 1; h <= HOUR_END; h += 1) {
    const label = `${String(h).padStart(2, '0')}h`;
    parts.push(`<div class="tpm-hour-col" data-tpm="hour-col" data-hour="${label}">
    <div class="tpm-hour-head"><span>${label}</span><span class="tpm-hour-count">0</span></div>
    <div class="tpm-hour-body"></div>
  </div>`);
  }

  return parts.join('');
}

function renderGantt(entries) {
  // OS (z baixo) → almoço → deslocamento (z alto)
  const osSegs = [];
  const lunchSegs = [];
  const travelSegs = [];

  entries.forEach((e) => {
    const startMin = parseHhMmToMinutes(e.horaIni);
    if (startMin == null) return;
    const left = leftPct(startMin);
    if (left == null) return;
    const w = widthPct(e.durMin ?? 15);
    if (e.kind === 'os') {
      if (e.emServico) {
        osSegs.push(
          `<div class="tpm-gantt-seg tpm-gantt-seg--os tpm-gantt-seg--inservice is-live" data-tpm="gantt-seg" data-kind="os" data-live="1" data-status="em-servico" data-tpm-tip="Em serviço #${esc(e.os || '')}" style="left:${left.toFixed(2)}%;width:${w.toFixed(2)}%">${WRENCH_SVG}</div>`
        );
      } else {
        osSegs.push(
          `<div class="tpm-gantt-seg tpm-gantt-seg--os" data-tpm="gantt-seg" data-kind="os" data-cond="${esc(e.condNome)}" data-tpm-tip="#${esc(e.os || '')}" style="left:${left.toFixed(2)}%;width:${w.toFixed(2)}%;background:${e.meta.color}"></div>`
        );
      }
    } else if (e.kind === 'lunch') {
      const liveClass = e.emAndamento ? ' is-live' : '';
      const tip = e.emAndamento
        ? `Almoço em andamento · desde ${esc(e.horaIni || '')}`
        : e.horaFim
          ? `Almoço ${esc(e.horaIni || '')} → ${esc(e.horaFim)}`
          : `Almoço · ${esc(e.dur)}`;
      lunchSegs.push(
        `<div class="tpm-gantt-seg tpm-gantt-seg--lunch${liveClass}" data-tpm="gantt-seg" data-kind="lunch" data-live="${e.emAndamento ? '1' : '0'}" data-tpm-tip="${tip}" style="left:${left.toFixed(2)}%;width:${w.toFixed(2)}%;background:${LUNCH_META.seg}"></div>`
      );
    } else {
      const liveClass = e.emAndamento ? ' is-live' : '';
      const redirectedCls = e.redirected ? ' is-redirected' : '';
      const meta = e.redirected ? REDIRECTED_TRAVEL_META : TRAVEL_META;
      const label = travelCardLabel(e);
      const travelTip = e.emAndamento
        ? `${esc(label)} · desde ${esc(e.horaIni || '')}`
        : e.horaFim
          ? `${esc(label)} ${esc(e.horaIni || '')} → ${esc(e.horaFim)}`
          : `${esc(label)} · ${esc(e.dur)}`;
      travelSegs.push(
        `<div class="tpm-gantt-seg tpm-gantt-seg--travel${liveClass}${redirectedCls}" data-tpm="gantt-seg" data-kind="travel" data-live="${e.emAndamento ? '1' : '0'}" data-redirected="${e.redirected ? '1' : '0'}" data-tpm-tip="${travelTip}" style="left:${left.toFixed(2)}%;width:${w.toFixed(2)}%;background:${meta.seg}"></div>`
      );
    }
  });

  const hours = [7, 9, 11, 13, 15, 17, 19]
    .map((h) => {
      const left = ((h - HOUR_START) / (HOUR_END - HOUR_START)) * 100;
      return `<span style="left:${left.toFixed(2)}%">${String(h).padStart(2, '0')}h</span>`;
    })
    .join('');

  return `<div class="tpm-gantt-track">${osSegs.join('')}${lunchSegs.join('')}${travelSegs.join('')}</div><div class="tpm-gantt-hours">${hours}</div>`;
}

function renderChips(allEntries, filterKey) {
  const counts = new Map();
  let travelCount = 0;
  let lunchCount = 0;
  allEntries.forEach((e) => {
    if (e.kind === 'travel') {
      travelCount += 1;
      return;
    }
    if (e.kind === 'lunch') {
      lunchCount += 1;
      return;
    }
    if (e.emServico || !e.condNome) return;
    const key = e.condNome;
    const prev = counts.get(key) || { n: 0, color: e.meta.color };
    prev.n += 1;
    counts.set(key, prev);
  });

  const chips = [];
  [...counts.entries()]
    .sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0], 'pt-BR'))
    .forEach(([label, c]) => {
      const on = filterKey === label;
      chips.push(
        `<button type="button" class="tpm-chip${on ? ' is-on' : ''}" data-tpm="filter-chip" data-filter="${esc(label)}"><span class="tpm-dot" style="background:${c.color}"></span>${esc(label)}<span class="tpm-chip-n" style="color:${c.color}">${c.n}</span></button>`
      );
    });
  if (lunchCount > 0) {
    const on = filterKey === FILTER_LUNCH;
    chips.push(
      `<button type="button" class="tpm-chip${on ? ' is-on' : ''}" data-tpm="filter-chip" data-filter="${FILTER_LUNCH}"><span class="tpm-dot" style="background:${LUNCH_META.color}"></span>${LUNCH_META.label}<span class="tpm-chip-n" style="color:${LUNCH_META.color}">${lunchCount}</span></button>`
    );
  }
  if (travelCount > 0) {
    const on = filterKey === FILTER_TRAVEL;
    chips.push(
      `<button type="button" class="tpm-chip${on ? ' is-on' : ''}" data-tpm="filter-chip" data-filter="${FILTER_TRAVEL}"><span class="tpm-dot" style="background:${TRAVEL_META.color}"></span>${TRAVEL_META.label}<span class="tpm-chip-n" style="color:${TRAVEL_META.color}">${travelCount}</span></button>`
    );
  }
  return chips.join('');
}

function renderMutedSummary(entries) {
  const osN = entries.filter((e) => e.kind === 'os').length;
  const travelN = entries.filter((e) => e.kind === 'travel').length;
  const lunchN = entries.filter((e) => e.kind === 'lunch').length;
  const prodMin = entries
    .filter((e) => e.kind === 'os' && e.durMin != null)
    .reduce((acc, e) => acc + e.durMin, 0);
  const travelMin = entries
    .filter((e) => e.kind === 'travel' && e.durMin != null)
    .reduce((acc, e) => acc + e.durMin, 0);
  const lunchMin = entries
    .filter((e) => e.kind === 'lunch' && e.durMin != null)
    .reduce((acc, e) => acc + e.durMin, 0);
  const fmt = (m) => {
    const h = Math.floor(m / 60);
    const mm = Math.round(m % 60);
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  let text = `07h → 19h · ${osN} atendimentos · ${fmt(prodMin)} produzidos · ${travelN} deslocamentos · ${fmt(travelMin)}`;
  if (lunchN > 0) text += ` · ${lunchN} almoço${lunchN === 1 ? '' : 's'} · ${fmt(lunchMin)}`;
  return text;
}

function minutesToClock(mins) {
  if (mins == null || !Number.isFinite(mins)) return '—';
  const h = Math.floor(mins / 60) % 24;
  const m = Math.round(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * KPIs do rail + progresso.
 */
export function computeMockRailKpis(entries, opts = {}) {
  const osClosed = (entries || []).filter((e) => e.kind === 'os' && !e.emServico);
  const osLive = (entries || []).filter((e) => e.kind === 'os' && e.emServico);
  const travels = (entries || []).filter((e) => e.kind === 'travel');
  const travelsDone = travels.filter((e) => !e.emAndamento && e.horaFim);

  const foraCount = osClosed.filter((e) => e.fora).length;
  const concluídas = osClosed.length;
  const pendencias = osClosed.filter((e) => e.hasPendencia).length;
  const openCount = Number(opts.openCount) || 0;
  const denom = Math.max(concluídas + openCount, 1);
  const pct = Math.min(100, Math.round((concluídas / denom) * 100));
  const barDone = Math.min(100, (concluídas / denom) * 100);
  const barPend = Math.min(100 - barDone, (pendencias / denom) * 100);

  const prodMin = osClosed
    .filter((e) => e.durMin != null)
    .reduce((acc, e) => acc + e.durMin, 0);
  const travelDoneMin = travelsDone
    .filter((e) => e.durMin != null)
    .reduce((acc, e) => acc + e.durMin, 0);

  const fmtHm = (m) => {
    const h = Math.floor(Math.max(0, m) / 60);
    const mm = Math.round(Math.max(0, m) % 60);
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };

  let dateLabel = '';
  try {
    const raw = opts.dateYmd && /^\d{4}-\d{2}-\d{2}$/.test(String(opts.dateYmd))
      ? String(opts.dateYmd)
      : null;
    const d = raw
      ? (() => {
          const [y, m, day] = raw.split('-').map((n) => parseInt(n, 10));
          return new Date(y, m - 1, day);
        })()
      : new Date();
    dateLabel = d.toLocaleDateString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    dateLabel = '';
  }

  const iniMins = [...osClosed, ...osLive]
    .map((e) => parseHhMmToMinutes(e.horaIni))
    .filter((m) => m != null)
    .sort((a, b) => a - b);
  const fimMins = osClosed
    .map((e) => parseHhMmToMinutes(e.horaFim))
    .filter((m) => m != null)
    .sort((a, b) => a - b);

  const firstCheckIn = iniMins.length ? minutesToClock(iniMins[0]) : '—';
  const lastOsFim = fimMins.length ? fimMins[fimMins.length - 1] : null;
  const lastCheckIn = lastOsFim != null ? minutesToClock(lastOsFim) : '—';

  let sincMin = lastOsFim;
  const events = [
    ...osClosed.map((e) => ({
      kind: 'os',
      sort: parseHhMmToMinutes(e.horaIni) ?? parseHhMmToMinutes(e.horaFim) ?? -1,
      end: parseHhMmToMinutes(e.horaFim),
    })),
    ...osLive.map((e) => ({
      kind: 'os',
      sort: parseHhMmToMinutes(e.horaIni) ?? -1,
      end: (() => {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
      })(),
    })),
    ...travels.map((e) => ({
      kind: 'travel',
      sort: parseHhMmToMinutes(e.horaIni) ?? -1,
      end: e.emAndamento
        ? (parseHhMmToMinutes(e.horaFim) ??
          (() => {
            const now = new Date();
            return now.getHours() * 60 + now.getMinutes();
          })())
        : parseHhMmToMinutes(e.horaFim) ?? parseHhMmToMinutes(e.horaIni),
      start: parseHhMmToMinutes(e.horaIni),
      emAndamento: Boolean(e.emAndamento),
    })),
  ].filter((ev) => ev.sort >= 0);

  events.sort((a, b) => a.sort - b.sort || (a.end ?? 0) - (b.end ?? 0));
  const lastEv = events.length ? events[events.length - 1] : null;
  if (lastEv?.kind === 'travel' && lastEv.end != null) {
    sincMin = lastEv.end;
  } else {
    travelsDone.forEach((t) => {
      const tStart = parseHhMmToMinutes(t.horaIni);
      const tEnd = parseHhMmToMinutes(t.horaFim) ?? tStart;
      if (tStart == null || tEnd == null) return;
      const hasOsAhead = osClosed.some((o) => {
        const oStart = parseHhMmToMinutes(o.horaIni);
        return oStart != null && oStart > tStart;
      });
      if (!hasOsAhead && (sincMin == null || tEnd > sincMin)) sincMin = tEnd;
    });
  }

  const sinc = sincMin != null ? minutesToClock(sincMin) : '—';
  let checkInHint = 'sem check-in';
  if (lastCheckIn !== '—') {
    checkInHint = sinc !== '—'
      ? `último às ${lastCheckIn} · sinc. ${sinc}`
      : `último às ${lastCheckIn}`;
  } else if (sinc !== '—') {
    checkInHint = `sinc. ${sinc}`;
  }

  const tempoHint =
    travelDoneMin > 0
      ? `+${fmtHm(travelDoneMin)} deslocamentos`
      : 'sem deslocamentos concluídos';

  return {
    foraCount,
    foraHint: foraCount > 0 ? `${foraCount} fora do roteiro` : 'todas no roteiro',
    foraHintWarn: foraCount > 0,
    firstCheckIn,
    lastCheckIn,
    sinc,
    checkInHint,
    tempoProduzido: fmtHm(prodMin),
    tempoHint,
    tempoHintOk: travelDoneMin > 0,
    progressPct: pct,
    progressLabel: dateLabel ? `progresso · ${dateLabel}` : 'progresso',
    barDone: `${barDone.toFixed(1)}%`,
    barPend: `${barPend.toFixed(1)}%`,
    legendDone: `${concluídas} concluída${concluídas === 1 ? '' : 's'}`,
    legendPend: `${pendencias} pendência${pendencias === 1 ? '' : 's'}`,
  };
}

function applyRailKpis(root, allEntries, opts = {}) {
  const rail = computeMockRailKpis(allEntries, opts);

  const progress = root.querySelector('[data-tpm="progress"]');
  if (progress) {
    const pctEl = progress.querySelector('.tpm-pct');
    const labelEl = progress.querySelector('.tpm-progress-label');
    const bar = progress.querySelector('.tpm-bar');
    const legend = progress.querySelector('.tpm-legend-row');
    if (pctEl) pctEl.textContent = `${rail.progressPct}%`;
    if (labelEl) labelEl.textContent = rail.progressLabel;
    if (bar) {
      const segs = bar.querySelectorAll('div');
      if (segs[0]) {
        segs[0].style.width = rail.barDone;
        segs[0].style.background = 'linear-gradient(90deg,#6d54e8,#8a72ff)';
      }
      if (segs[1]) {
        segs[1].style.width = rail.barPend;
        segs[1].style.background = '#c9f0e0';
      }
    }
    if (legend) {
      legend.innerHTML = `<span><span class="tpm-dot" style="background:#6d54e8"></span>${rail.legendDone}</span><span><span class="tpm-dot" style="background:#c9f0e0"></span>${rail.legendPend}</span>`;
    }
  }

  const kpis = root.querySelectorAll('[data-tpm="kpi"]');
  if (!kpis.length) return;

  const foraHint = kpis[0]?.querySelector('.tpm-kpi-hint');
  if (foraHint) {
    foraHint.textContent = rail.foraHint;
    foraHint.classList.toggle('is-warn', !!rail.foraHintWarn);
    foraHint.classList.toggle('is-ok', false);
    foraHint.classList.toggle('is-muted', !rail.foraHintWarn);
    foraHint.style.removeProperty('color');
  }

  const tempoValue = kpis[2]?.querySelector('.tpm-kpi-value');
  const tempoHint = kpis[2]?.querySelector('.tpm-kpi-hint');
  if (tempoValue) tempoValue.textContent = rail.tempoProduzido;
  if (tempoHint) {
    tempoHint.textContent = rail.tempoHint;
    tempoHint.classList.toggle('is-ok', !!rail.tempoHintOk);
    tempoHint.classList.toggle('is-warn', false);
    tempoHint.classList.toggle('is-muted', !rail.tempoHintOk);
    tempoHint.style.removeProperty('color');
  }

  const checkInValue = kpis[3]?.querySelector('.tpm-kpi-value');
  const checkInHint = kpis[3]?.querySelector('.tpm-kpi-hint');
  if (checkInValue) checkInValue.textContent = rail.firstCheckIn;
  if (checkInHint) {
    checkInHint.textContent = rail.checkInHint;
    checkInHint.classList.add('is-muted');
    checkInHint.classList.remove('is-ok', 'is-warn');
    checkInHint.style.removeProperty('color');
  }
}

/**
 * Aplica snapshot do dia no DOM do mock (board, gantt, chips, resumo).
 * @param {HTMLElement} root
 * @param {{ os?: object[], deslocamentos?: object[], almoco?: object[] } | null} dayData
 * @param {{ filterKey?: string|null, onFilterChange?: (key: string|null) => void, openCount?: number, onOsClick?: (entry: object) => void, selectedOs?: string|null }} [opts]
 */
export function clearTechnicianPanelDayMockDom(root) {
  if (!root) return;
  if (typeof root.__tpmTipCleanup === 'function') {
    root.__tpmTipCleanup();
    root.__tpmTipCleanup = null;
  }
  const board = root.querySelector('[data-tpm="board"]');
  if (board) board.innerHTML = '';
  const gantt = root.querySelector('[data-tpm="gantt"]');
  if (gantt) gantt.innerHTML = '';
  root.querySelectorAll('[data-tpm="filter-chip"]').forEach((el) => el.remove());
  const muted = root.querySelector('.tpm-toolbar .tpm-muted');
  if (muted) muted.textContent = 'Carregando…';
  const progress = root.querySelector('[data-tpm="progress"]');
  if (progress) {
    const pctEl = progress.querySelector('.tpm-pct');
    const labelEl = progress.querySelector('.tpm-progress-label');
    if (pctEl) pctEl.textContent = '—';
    if (labelEl) labelEl.textContent = 'progresso';
    const bar = progress.querySelector('.tpm-bar');
    if (bar) {
      const segs = bar.querySelectorAll('div');
      if (segs[0]) segs[0].style.width = '0%';
      if (segs[1]) segs[1].style.width = '0%';
    }
    const legend = progress.querySelector('.tpm-legend-row');
    if (legend) legend.innerHTML = '';
  }
}

export function applyTechnicianPanelDayToMockDom(root, dayData, opts = {}) {
  if (!root) return;
  if (typeof root.__tpmTipCleanup === 'function') {
    root.__tpmTipCleanup();
    root.__tpmTipCleanup = null;
  }
  const filterKey = opts.filterKey ?? null;
  const onFilterChange = opts.onFilterChange;
  const onOsClick = opts.onOsClick;
  const selectedOs = opts.selectedOs != null ? String(opts.selectedOs) : null;

  const allEntries = buildEntries(dayData);
  const visible = filterEntries(allEntries, filterKey);

  applyRailKpis(root, allEntries, { openCount: opts.openCount, dateYmd: opts.dateYmd });

  const board = root.querySelector('[data-tpm="board"]');
  if (board) board.innerHTML = renderBoard(visible);

  const gantt = root.querySelector('[data-tpm="gantt"]');
  if (gantt) gantt.innerHTML = renderGantt(visible);

  const toolbar = root.querySelector('.tpm-toolbar');
  if (toolbar) {
    toolbar.querySelectorAll('[data-tpm="filter-chip"]').forEach((el) => el.remove());
    const flex = toolbar.querySelector('.tpm-flex');
    const chipsHtml = renderChips(allEntries, filterKey);
    if (flex && chipsHtml) {
      flex.insertAdjacentHTML('afterend', chipsHtml);
    } else if (chipsHtml) {
      toolbar.insertAdjacentHTML('beforeend', chipsHtml);
    }
    const muted = toolbar.querySelector('.tpm-muted');
    if (muted) muted.textContent = renderMutedSummary(allEntries);

    if (typeof onFilterChange === 'function') {
      toolbar.querySelectorAll('[data-tpm="filter-chip"]').forEach((btn) => {
        btn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const key = btn.getAttribute('data-filter');
          onFilterChange(filterKey === key ? null : key);
        });
      });
    }
  }

  // Clique em OS fechada → drawer mock
  if (board && typeof onOsClick === 'function') {
    board.querySelectorAll('.tpm-card-os.is-clickable').forEach((card) => {
      const osNum = card.getAttribute('data-os');
      if (selectedOs && osNum === selectedOs) card.classList.add('is-selected');
      const open = () => {
        const entry = allEntries.find((e) => e.kind === 'os' && !e.emServico && String(e.os) === String(osNum));
        if (entry) onOsClick(entry);
      };
      card.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        open();
      });
      card.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          open();
        }
      });
    });
  }

  if (typeof root.__tpmTipCleanup === 'function') {
    root.__tpmTipCleanup();
    root.__tpmTipCleanup = null;
  }
  root.__tpmTipCleanup = bindTechnicianPanelDomTips(root);
}
